/**
 * @module Controller
 */
/* eslint-disable no-invalid-this */
import { View, findOrCreate } from "./view";
import debounce from "lodash/debounce";
import { ApiCache } from "./cache";
import { addStyle } from "./css";
import { Client, checkKeyUsability } from "@ideal-postcodes/core-axios";
import { getScope, getDocument, populateAddress, } from "@ideal-postcodes/jsutil";
/**
 * @hidden
 */
export const NOOP = () => { };
/**
 * Default options assigned to controller instances
 */
export const defaults = {
    // DOM
    outputScope: null,
    // Client
    apiKey: "",
    checkKey: true,
    // WAI-ARIA compliance settings
    aria: "1.0",
    // Behaviour
    titleizePostTown: true,
    outputFields: {},
    names: {},
    labels: {},
    removeOrganisation: false,
    injectStyle: true,
    inputField: "",
    autocomplete: "none",
    populateCounty: true,
    populateOrganisation: true,
    queryOptions: {},
    // Messages
    msgFallback: "Please enter your address manually",
    msgInitial: "Start typing to find address",
    msgNoMatch: "No matches found",
    msgList: "Select your address",
    // View classes
    messageClass: "idpc_error",
    containerClass: "idpc_autocomplete",
    listClass: "idpc_ul",
    // Syles
    inputStyle: {},
    listStyle: {},
    liStyle: {},
    containerStyle: {},
    // Hide / unhide
    unhide: null,
    unhideClass: "idpc-unhide",
    msgUnhide: "Enter address manually",
    hide: [],
    // Callbacks
    onOpen: NOOP,
    onSelect: NOOP,
    onBlur: NOOP,
    onClose: NOOP,
    onFocus: NOOP,
    onInput: NOOP,
    onLoaded: NOOP,
    onSearchError: NOOP,
    onSuggestionError: NOOP,
    onMounted: NOOP,
    onRemove: NOOP,
    onSuggestionsRetrieved: NOOP,
    onAddressSelected: NOOP,
    onAddressRetrieved: NOOP,
    onAddressPopulated: NOOP,
    onFailedCheck: NOOP,
    onMouseDown: NOOP,
    onKeyDown: NOOP,
    onUnhide: NOOP,
};
/**
 * # Controller
 *
 * The Autocomplete Controller class acts as the public class which you may
 * wield to enable address autocomplete on your HTML address forms
 *
 * When instantiated, the controller will serve as a bridge beteen the
 * address suggestion view presented on the DOM and the Ideal
 * Postcodes Address resolution HTTP APIs
 *
 * More concretely, the instantiation of a controller instance creates:
 * - A user interface instance `View`
 * - An instance of the [Ideal Postcodes Browser Client](https://github.com/ideal-postcodes/core-axios)
 *
 * The role of the controller is to bind to events produced by the user
 * interface and take appropriate action including querying the API,
 * modifying other aspects of the DOM.
 */
export class Controller {
    constructor(options) {
        this.options = {
            ...{ scope: window.document, document: window.document },
            ...defaults,
            ...options,
        };
        // Default inputField to line_1 if `inputField` not specified
        if (!options.inputField)
            this.options.inputField = this.options.outputFields.line_1 || "";
        // To overcome config overload - idpcConfig global config object already
        // defines autocomplete (boolean)
        //@ts-ignore
        if (this.options.autocomplete === true)
            this.options.autocomplete = defaults.autocomplete;
        // Scope the operations of this controller to a document or DOM subtree
        this.scope = getScope(this.options.scope);
        // Assign a parent Document for elem creation
        this.document = getDocument(this.scope);
        // Assign a document or DOM subtree to scope outputs. Defaults to controller scope
        this.outputScope = findOrCreate(this.scope, this.options.outputScope, () => this.scope);
        this.client = new Client({ ...this.options, api_key: this.options.apiKey });
        this.cache = new ApiCache(this.client);
        this.view = new View({
            ...this.options,
            scope: this.scope,
            document: this.document,
            onInput: debounce(this._onInput(), 100, {
                leading: true,
                trailing: true,
                maxWait: 100,
            }),
            onSelect: this._onSelect(),
        });
        this.init();
    }
    /**
     * Binds to DOM and begin DOM mutations
     *
     * @hidden
     */
    load() {
        this.view.attach();
        addStyle(this);
        this.options.onLoaded.call(this);
    }
    /**
     * Attaches Controller to the DOM.
     *
     * If `checkKey` is enabled, a key check will be performed prioer to binding. Use the `onLoaded` and `onFailedCheck` callbacks to define follow up behaviour if the key check succeeds or fails
     */
    init() {
        return new Promise((resolve) => {
            if (!this.options.checkKey) {
                this.load();
                resolve();
                return;
            }
            checkKeyUsability({ client: this.client, api_key: this.options.apiKey })
                .then((response) => {
                if (!response.available)
                    throw new Error("Key currently not usable");
                this.load();
                resolve();
            })
                .catch((error) => {
                this.options.onFailedCheck.call(this, error);
                resolve();
            });
        });
    }
    /**
     * Produces a function to be bound to an instance of `Autocomplete.View`.
     * It executes suggestion search when address input is updated
     *
     * @private
     */
    _onInput() {
        const self = this;
        return function (event) {
            self.options.onInput.call(this, event);
            const query = this.query();
            if (query.trim().length === 0) {
                this.setMessage(self.options.msgInitial);
                return Promise.resolve(this);
            }
            return self.cache
                .query(query, self.options.queryOptions)
                .then((suggestions) => {
                self.options.onSuggestionsRetrieved.call(self, suggestions);
                return this.setSuggestions(suggestions, query);
            })
                .catch((error) => {
                if (this.query() === query)
                    this.setMessage(self.options.msgFallback);
                self.options.onSuggestionError.call(self, error);
                return self.view;
            });
        };
    }
    /**
     * Produces a function to be bound to an instance of `Autocomplete.View`.
     * Populates fields with correct address when suggestion selected
     *
     * @private
     */
    _onSelect() {
        const self = this;
        return function (suggestion) {
            self.options.onAddressSelected.call(self, suggestion);
            return self.cache
                .resolve(suggestion)
                .then((address) => {
                if (address === null)
                    throw "Unable to retrieve address";
                self.options.onAddressRetrieved.call(self, address);
                self.populateAddress(address);
                return this;
            })
                .catch((error) => {
                this.open();
                this.setMessage(self.options.msgFallback);
                self.options.onSearchError.call(self, error);
                return error;
            });
        };
    }
    /**
     * Writes a selected to the input fields specified in the controller config
     *
     * @public
     */
    populateAddress(address) {
        this.view.unhideFields();
        populateAddress({
            address,
            config: { ...this.options, scope: this.outputScope },
            outputFields: this.options.outputFields,
            names: this.options.names,
            labels: this.options.labels,
        });
        this.options.onAddressPopulated.call(this, address);
    }
    /**
     * Applies new query options to search. This process clears the existing
     * cache to prevent stale searches
     *
     * @public
     */
    setQueryOptions(options) {
        this.cache.clear();
        this.options.queryOptions = options;
    }
}
